import React, { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeftIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { apiClient } from '../lib/apiClient';
import AccountRadarView from '../components/insights/AccountRadarView';
import PipelineLoadingOverlay from '../components/PipelineLoadingOverlay';
import { AccountRadarResponse, isRadarResponse } from '../components/insights/types';

const RADAR_QUERY = 'Which customers need attention this week?';

interface InsightsRouterResponse {
  action?: string;
  path?: string;
  state?: AccountRadarResponse;
}

const AccountRadarPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const stateFromNav = useMemo(() => {
    const state = location.state?.mcpResponse || location.state;
    if (state && isRadarResponse(state)) {
      return state as AccountRadarResponse;
    }
    return null;
  }, [location.state]);

  const {
    data: fetchedRadar,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useQuery({
    queryKey: ['account-radar'],
    queryFn: async (): Promise<AccountRadarResponse> => {
      const response = await apiClient.customRequest<InsightsRouterResponse>(
        '/api/insights/query',
        {
          method: 'POST',
          body: { query: RADAR_QUERY, context_type: 'general' },
        }
      );

      const payload = response.state;
      if (payload && isRadarResponse(payload)) {
        return payload;
      }

      throw new Error('Unexpected response from account radar');
    },
    enabled: !stateFromNav,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  const radarData = stateFromNav ?? fetchedRadar ?? null;
  const showLoading = !stateFromNav && (isLoading || isFetching) && !radarData;

  return (
    <div className="min-h-screen bg-background">
      <PipelineLoadingOverlay
        isVisible={showLoading}
        requestType="account_radar"
        query={RADAR_QUERY}
      />

      <div className="bg-card border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="flex items-center text-muted-foreground hover:text-foreground"
            >
              <ArrowLeftIcon className="h-5 w-5 mr-2" />
              Back
            </button>
            <h1 className="text-xl font-semibold">Account Radar</h1>
          </div>
          {!stateFromNav && (
            <button
              type="button"
              onClick={() => refetch()}
              disabled={isFetching}
              className="inline-flex items-center gap-2 rounded-lg border border-border px-3 py-1.5 text-sm text-foreground hover:bg-muted disabled:opacity-50"
            >
              <ArrowPathIcon className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && !radarData && (
          <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-6 text-center">
            <p className="text-destructive mb-4">Failed to load account radar.</p>
            <button
              type="button"
              onClick={() => refetch()}
              className="rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground hover:bg-primary/90"
            >
              Retry
            </button>
          </div>
        )}

        {radarData && <AccountRadarView data={radarData} />}

        {!showLoading && !error && !radarData && (
          <div className="text-center py-16 text-muted-foreground">
            <p>No accounts need immediate attention right now.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AccountRadarPage;
